import { WifiOff } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { settingsStore, useSettings } from '../lib/settings';
import { toast } from '../lib/toast';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';
import { SettingsDialog, type SettingsTab } from '../settings/SettingsDialog';
import { BreakoutBar, BreakoutPanel } from './BreakoutPanel';
import { ChatPanel } from './ChatPanel';
import { InviteDialog, LeaveDialog, UnmuteRequestDialog } from './dialogs';
import { ParticipantsPanel } from './ParticipantsPanel';
import { RenameDialog } from './ParticipantMenu';
import { closeFloating, openFloating } from './floating';
import { RecordingBar } from './RecordingBar';
import { RedistributionBar, RedistributionBoard, RedistributionPanel } from './Redistribution';
import { ShareBar } from './ShareBar';
import { ShareDialog } from './ShareDialog';
import { Stage, type ViewMode } from './Stage';
import { Toolbar } from './Toolbar';
import { TopBar } from './TopBar';

type Dialog = { kind: 'leave' } | { kind: 'invite' } | { kind: 'rename' } | { kind: 'settings'; tab: SettingsTab } | { kind: 'share'; mode: 'new' | 'change' } | null;

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
}

export function MeetingRoom({ session }: { session: MeetingSession }) {
  const s = useStore(session.store);
  const settings = useSettings();
  const [view, setView] = useState<ViewMode>('gallery');
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [breakoutOpen, setBreakoutOpen] = useState(false);
  const [redisOpen, setRedisOpen] = useState(false);
  const [boardHidden, setBoardHidden] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [fullscreen, setFullscreen] = useState(!!document.fullscreenElement);
  const pushToTalk = useRef(false);
  const self = s.participants[s.selfId!];

  useEffect(() => {
    document.title = `${s.meeting?.title ?? 'Reunión'} · Nexo Meet`;
  }, [s.meeting?.title]);

  // In the room, notifications move above the toolbar so they never cover the side panels.
  useEffect(() => {
    document.body.classList.add('in-room');
    return () => document.body.classList.remove('in-room');
  }, []);

  // Chrome can open the floating window by itself when you switch tabs during a call
  // ("automatic picture-in-picture" for video conferencing). Close it when leaving the room.
  useEffect(() => {
    const ms = navigator.mediaSession as MediaSession & { setActionHandler(action: string, handler: (() => void) | null): void };
    try {
      ms?.setActionHandler('enterpictureinpicture', () => void openFloating(session));
    } catch {
      /* not supported by this browser */
    }
    return () => {
      try {
        ms?.setActionHandler('enterpictureinpicture', null);
      } catch {
        /* ignore */
      }
      closeFloating();
    };
  }, [session]);

  const toggleFullscreen = useCallback(() => {
    // iPhone Safari has no Fullscreen API for pages.
    if (!document.documentElement.requestFullscreen) return void toast('Este navegador no permite la pantalla completa', 'warning');
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const requestLeave = useCallback(() => {
    const amHost = session.isHost;
    if (!amHost && !settingsStore.get().confirmLeave) session.leave();
    else setDialog({ kind: 'leave' });
  }, [session]);

  // Keyboard shortcuts (Alt + letter, matched on the physical key so macOS Option works too).
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        const actions: Record<string, () => void> = {
          KeyA: () => void session.toggleAudio(),
          KeyV: () => void session.toggleVideo(),
          KeyS: () => (session.store.get().screenStream ? session.stopShare() : setDialog({ kind: 'share', mode: 'new' })),
          KeyH: () => setChatOpen((v) => !v),
          KeyU: () => setParticipantsOpen((v) => !v),
          KeyY: () => {
            const st = session.store.get();
            session.setHandRaised(!st.participants[st.selfId!]?.handRaised);
          },
          KeyF: toggleFullscreen,
        };
        const run = actions[e.code];
        if (run) {
          e.preventDefault();
          run();
        }
        return;
      }
      // Hold space to talk while muted.
      if (e.code === 'Space' && !e.repeat && !isTyping(e.target) && session.store.get().audioMuted && !dialog) {
        e.preventDefault();
        pushToTalk.current = true;
        void session.toggleAudio();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && pushToTalk.current) {
        pushToTalk.current = false;
        if (!session.store.get().audioMuted) void session.toggleAudio();
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [session, toggleFullscreen, dialog]);

  // A redistribution that starts (or starts again) is shown to everyone.
  const redisStatus = s.redistribution.status;
  const redisStartedAt = s.redistribution.startedAt;
  useEffect(() => {
    if (redisStatus !== 'off') setBoardHidden(false);
    else setRedisOpen(false);
  }, [redisStatus, redisStartedAt]);
  const boardShown = redisStatus !== 'off' && !boardHidden;

  const sharingBorder = !!s.screenStream && settings.shareBorder;
  const disconnectedPeers = Object.entries(s.peerStats).filter(([, st]) => st.state === 'failed').length;

  return (
    <div className={`room meeting-theme ${sharingBorder ? 'sharing-border' : ''}`}>
      <TopBar session={session} view={view} onToggleView={() => setView((v) => (v === 'gallery' ? 'speaker' : 'gallery'))} fullscreen={fullscreen} onToggleFullscreen={toggleFullscreen} />
      <div className="room-body">
        <main className="stage" aria-label="Vídeo de la reunión">
          <div className="stage-bars">
            {s.screenStream && <ShareBar session={session} onChange={() => setDialog({ kind: 'share', mode: 'change' })} />}
            <RecordingBar session={session} />
            <BreakoutBar session={session} />
            <RedistributionBar session={session} boardShown={boardShown} onToggleBoard={() => setBoardHidden(false)} />
          </div>
          <Stage
            session={session}
            view={view}
            board={boardShown ? <RedistributionBoard session={session} onConfigure={() => setRedisOpen(true)} onHide={() => setBoardHidden(true)} /> : undefined}
          />
          {s.connection !== 'online' ? (
            <div className={`banner ${s.connection === 'offline' ? 'banner-error' : 'banner-warn'}`} role="alert">
              <WifiOff size={16} />
              {s.connection === 'offline' ? 'Sin conexión a Internet. Volveremos a conectar en cuanto vuelva.' : 'Reconectando con la reunión…'}
            </div>
          ) : (
            disconnectedPeers > 0 && (
              <div className="banner banner-warn" role="status">
                <WifiOff size={16} />
                No se puede establecer la conexión de medios con {disconnectedPeers === 1 ? 'un participante' : `${disconnectedPeers} participantes`}.{' '}
                {session.turnConfigured ? 'Reintentando…' : 'Su red bloquea las conexiones directas; quien administra Nexo debe activar un servidor TURN.'}
              </div>
            )
          )}
        </main>
        {(participantsOpen || chatOpen || (breakoutOpen && session.isHost) || (redisOpen && session.isHost)) && (
          <aside className="side-panels">
            {redisOpen && session.isHost && <RedistributionPanel session={session} onClose={() => setRedisOpen(false)} />}
            {breakoutOpen && session.isHost && <BreakoutPanel session={session} onClose={() => setBreakoutOpen(false)} />}
            {participantsOpen && <ParticipantsPanel session={session} onClose={() => setParticipantsOpen(false)} onInvite={() => setDialog({ kind: 'invite' })} />}
            {chatOpen && <ChatPanel session={session} onClose={() => setChatOpen(false)} />}
          </aside>
        )}
      </div>
      <Toolbar
        session={session}
        participantsOpen={participantsOpen}
        chatOpen={chatOpen}
        breakoutOpen={breakoutOpen}
        onToggleBreakout={() => setBreakoutOpen((v) => !v)}
        redisOpen={redisOpen}
        onToggleRedis={() => setRedisOpen((v) => !v)}
        boardShown={boardShown}
        onToggleBoard={() => setBoardHidden((v) => !v)}
        onToggleParticipants={() => setParticipantsOpen((v) => !v)}
        onToggleChat={() => setChatOpen((v) => !v)}
        onOpenSettings={(tab) => setDialog({ kind: 'settings', tab })}
        onLeave={requestLeave}
        onRename={() => setDialog({ kind: 'rename' })}
        onInvite={() => setDialog({ kind: 'invite' })}
        onOpenShare={() => setDialog({ kind: 'share', mode: 'new' })}
        onToggleView={() => setView((v) => (v === 'gallery' ? 'speaker' : 'gallery'))}
        onToggleFullscreen={toggleFullscreen}
      />

      {dialog?.kind === 'leave' && <LeaveDialog session={session} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'invite' && <InviteDialog session={session} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'share' && <ShareDialog session={session} mode={dialog.mode} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'rename' && self && <RenameDialog current={self.name} onClose={() => setDialog(null)} onSave={(name) => session.rename(name)} />}
      {dialog?.kind === 'settings' && <SettingsDialog initialTab={dialog.tab} onClose={() => setDialog(null)} />}
      {s.unmuteRequestBy && s.audioMuted && <UnmuteRequestDialog session={session} byName={s.unmuteRequestBy} />}
    </div>
  );
}
